package com.islamicmedia.shared.domain.model

import com.islamicmedia.shared.util.currentTimeMillis
import com.islamicmedia.shared.util.parseTimestampMillis
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * The user's billing entitlement, mirrored from the server's `user_entitlements` row
 * (GET /api/account/entitlement). See PRD_SUBSCRIPTION_BILLING.md.
 *
 * Dual carry-over balances: [fullCredits] (full searches) and [qcCredits] (quick checks).
 * Free tier = one lifetime full search ([freeFullUsed]). Legacy $100/yr users have
 * [unlimitedUntil] set. The server is authoritative on gating; this drives client UX only.
 */
@Serializable
data class Entitlement(
    @SerialName("full_credits") val fullCredits: Int = 0,
    @SerialName("qc_credits") val qcCredits: Int = 0,
    /** True once ALL the free searches are spent. It stays false until the last one: the 3.4.0 apps
     *  read only this flag, and the server relies on that so they are never blocked early. */
    @SerialName("free_full_used") val freeFullUsed: Boolean = false,
    /** How many free full searches an account gets (3), and how many are left. Null from a server that
     *  predates 2026-10-14_three_free_searches.sql: then it is the old single free search. */
    @SerialName("free_full_limit") val freeFullLimit: Int? = null,
    @SerialName("free_full_remaining") val freeFullRemaining: Int? = null,
    @SerialName("free_qc_used") val freeQcUsed: Boolean = false,
    @SerialName("unlimited_until") val unlimitedUntil: String? = null,
    val plan: String? = null,
    val status: String = "free",
    @SerialName("current_period_end") val currentPeriodEnd: String? = null,
    /** List price per billing period in cents, as Stripe reports it (e.g. `thrivecart_9` members
     *  no longer all pay the same amount). Null on servers that predate this field. */
    @SerialName("plan_amount_cents") val planAmountCents: Int? = null,
    /** Billing period for [planAmountCents]: `"month"`, `"year"`, or `"3 months"` when the
     *  interval count is above 1. Null on servers that predate this field. */
    @SerialName("plan_interval") val planInterval: String? = null,
    /** Fair-use limit on analyses per rolling window (7 days) for this account; null = no limit.
     *  Absent on servers that predate the weekly cap. The keys are `cap_*`, not the old `daily_*`:
     *  the 3.4.0 apps read `daily_*` and would word a weekly value as a daily one.
     *  See 2026-10-07_weekly_cap_and_search_packs.sql. */
    @SerialName("cap_limit") val capLimit: Int? = null,
    /** Analyses counted against [capLimit] in the current window. */
    @SerialName("cap_used") val capUsed: Int = 0,
    /** ISO-8601 moment the next analysis frees up. Set by the server only while at the limit. */
    @SerialName("cap_resets_at") val capResetsAt: String? = null,
    /** Length of the rolling window in days (7 = weekly). */
    @SerialName("cap_window_days") val capWindowDays: Int = 7,
    /** Searches left in this account's search packs (organisations; set up by hand in admin, never
     *  sold in the app). Sent by the server ONLY for an account that holds a pack, else absent (0). */
    @SerialName("pack_remaining") val packRemaining: Int = 0,
    /** ISO-8601 expiry of the soonest-expiring pack that still has searches; null = never expires. */
    @SerialName("pack_expires_at") val packExpiresAt: String? = null,
    /** Every search granted on the live packs, so the profile can read "9/250" (used/total). */
    @SerialName("pack_total") val packTotal: Int = 0,
    /** May this member refer friends? Server-computed (referral_can_refer): paying on either
     *  Stripe account, or a founding member. Drives the "Refer a friend" menu item. Use this, NOT
     *  [isActiveSubscriber], which is also true for a gifted invite that must not refer. Absent
     *  (so false) when the program is off, the check failed, or the server predates referrals. */
    @SerialName("can_refer") val canRefer: Boolean = false,
    /** Has the owner switched KIDZONE (Kids Mode) on for this account? OFF for everyone by default;
     *  switched on per account in the localhost admin. The server sends it ONLY as `true` and
     *  refuses every kids request itself otherwise, so this just drives the menu item and the
     *  parent-side preview. Absent (so false) on any other account, on a failed check, or on a
     *  server that predates the allowlist. See 2026-10-11_kids_mode_allowlist.sql. */
    @SerialName("kids_mode") val kidsMode: Boolean = false,
    /** Has the owner switched bulk spine scan on for this account? OFF for everyone by default;
     *  switched on per account in the localhost admin, and sent ONLY as `true`. The server refuses
     *  the scan endpoints itself otherwise (and also needs a search pack), so this only drives the
     *  menu item. Absent (so false) on any other account or on a server that predates it.
     *  See 2026-10-17_bulk_scan_allowlist.sql. */
    @SerialName("bulk_scan") val bulkScan: Boolean = false,
    /** KIDZONE is open to EVERY signed-in account on this server, subscriber or not. Only a DEV server
     *  sends this (KIDS_MODE_FOR_ALL, ignored by a production deploy); PROD never does, so it is false
     *  there and KIDZONE keeps requiring [isActiveSubscriber] on top of [kidsMode]. */
    @SerialName("kids_mode_open") val kidsModeOpen: Boolean = false,
    /** Bulk spine scan is open to EVERY signed-in account on this server, with or without a search pack.
     *  Only a DEV server sends this (BULK_SCAN_FOR_ALL, ignored by a production deploy); PROD never
     *  does, so it is false there and bulk scan keeps requiring [isLibrary] on top of [bulkScan]. */
    @SerialName("bulk_scan_open") val bulkScanOpen: Boolean = false,
) {
    /**
     * Non-null when this account is at its weekly limit right now. Drives the pre-flight check that
     * stops a capped user before the paid disambiguation search. Client UX only: the server's
     * claim_daily_analysis is the real gate, so this fails OPEN (unknown limit or reset time, or a
     * reset time already in the past because this snapshot is stale, means "not capped"). An account
     * holding a search pack is never stopped here: the pack pays once the limit is reached.
     */
    fun dailyLimitNotice(nowMs: Long = currentTimeMillis()): DailyLimitNotice? {
        if (isLibrary) return null
        val limit = capLimit ?: return null
        if (capUsed < limit) return null
        val resetsAt = capResetsAt?.let { parseTimestampMillis(it) } ?: return null
        if (resetsAt <= nowMs) return null
        return DailyLimitNotice(limit = limit, resetsAtMs = resetsAt, windowDays = capWindowDays)
    }

    /**
     * May this account use KIDZONE? The owner switched it on ([kidsMode]) AND it is a subscriber (a child
     * must never reach a paywall), unless this server opens it to everyone ([kidsModeOpen], DEV only).
     */
    val canUseKidsMode: Boolean get() = kidsMode && (isActiveSubscriber || kidsModeOpen)

    /**
     * May this account use bulk scan? The owner switched it on ([bulkScan]) AND it is a library account
     * (holds a search pack), unless this server opens it to everyone ([bulkScanOpen], DEV only).
     */
    val canUseBulkScan: Boolean get() = bulkScan && (isLibrary || bulkScanOpen)

    /**
     * An organisation account (a school or library): it has been granted a search pack, used up or
     * not. These accounts are set up by hand, never subscribe, and are NOT subject to the weekly
     * cap, so the app shows them "Library" and "used/total" and, when they run out, tells them to
     * email support instead of offering a subscription. [hasPack] is the narrower "has searches left".
     */
    val isLibrary: Boolean get() = packTotal > 0 || hasPack

    /**
     * True when this account holds a search pack with searches left that has not expired. Packs are
     * set up by hand for schools and libraries; the server sends [packRemaining] only to holders.
     * An unparseable expiry fails OPEN (the server's consume_credit is the real gate).
     */
    val hasPack: Boolean get() {
        if (packRemaining <= 0) return false
        val expires = packExpiresAt?.let { parseTimestampMillis(it) } ?: return true
        return expires > currentTimeMillis()
    }

    /**
     * Unlimited access — legacy grandfathered users (status "legacy"), a comped invite membership
     * (status "gifted"), OR an active/canceling subscription on an unlimited plan (the server sets
     * [unlimitedUntil] to the period end each renewal).
     *
     * The DATE IS CHECKED, not just its presence. This used to test `unlimitedUntil != null` alone
     * and defer to the server for the real expiry — but a lapsed gift keeps status "gifted" and a
     * non-null date forever, so an expired member was still shown unlimited access here, sailed
     * past the paywall, and only discovered it when the server refused the search with a 402.
     * (Observed: a gift that ran out on 2026-09-04 still reading as live three days later.)
     */
    val isUnlimited: Boolean get() {
        if (status != "legacy" && status != "gifted" && status != "active" &&
            status != "canceling" && status != "paused"
        ) return false
        val until = unlimitedUntil ?: return false
        // Fail OPEN on an unparseable date: wrongly locking out a paying member is a worse failure
        // than briefly over-granting one, and the server's consume_credit is the real gate anyway.
        val expiresAt = parseTimestampMillis(until) ?: return true
        return expiresAt > currentTimeMillis()
    }

    /**
     * A complimentary membership from an admin invite (see the invitations table). Like "legacy",
     * it's unlimited-with-an-expiry and has NO Stripe subscription behind it — so the UI must never
     * offer these users the billing portal (it would 400: "No subscription to manage").
     */
    val isGifted: Boolean get() = status == "gifted"

    /**
     * Billing is paused — they took the "pause for a month" offer in the cancellation flow.
     *
     * The two dates mean DIFFERENT things while this is true: [unlimitedUntil] is the day they
     * have already paid through (access simply runs out when it passes, via [isUnlimited]'s date
     * check), and [currentPeriodEnd] is the day collection RESUMES. That is the whole mechanism —
     * there is no separate "access revoked" flag, and nothing has to run on a schedule.
     */
    val isPaused: Boolean get() = status == "paused"

    /** Inside a pause, but still within the month they already paid for. */
    val isPausePending: Boolean get() = isPaused && isUnlimited

    /**
     * Whether the in-app cancellation flow applies to this member.
     *
     * Only a live, self-serve Stripe subscription in OUR account can be surveyed, offered a
     * discount, paused or cancelled. Everyone else is deliberately excluded:
     *  - legacy / gifted have no subscription at all (see [isGifted]);
     *  - `thrivecart_9` bills through a separate Stripe account whose key is read-only for
     *    subscriptions, so every write the flow makes would fail;
     *  - already `canceling` means they're on the way out and there is nothing left to cancel.
     */
    val canCancelInApp: Boolean get() =
        (status == "active" || status == "past_due") && plan != null &&
            plan != "legacy" && plan != "thrivecart_9"

    /**
     * The one free FULL analysis for a registered, non-subscribed user (lifetime, not per-period).
     * Mirrors the server's `consume_credit` rule exactly — see 2026-08-03_free_full_analysis.sql.
     *
     * A free QUICK check does NOT exist: a non-subscriber's single free search is always the full
     * one, so [freeQcAvailable] is permanently false (the column still round-trips to the admin
     * dashboard, and [freeQcUsed] is deliberately NOT consulted here — someone whose only free
     * search was a pre-07-23 quick check still gets the full one).
     */
    /** Free full searches still unspent. */
    val freeFullsLeft: Int get() = freeFullRemaining ?: if (freeFullUsed) 0 else 1
    val freeFullAvailable: Boolean get() = freeFullsLeft > 0
    val freeQcAvailable: Boolean get() = false

    /** Whether the user can start a full search right now (optimistic UI gate). */
    val canFull: Boolean get() = isUnlimited || hasPack || freeFullAvailable

    /** Whether the user can start a quick check right now (optimistic UI gate).
     *  Quick check is subscriber-only — it is never covered by the free analysis. */
    val canQuick: Boolean get() = isUnlimited || hasPack

    /**
     * Effective AVAILABLE counts — the single source of truth for "how many searches does this user
     * have right now", consumed by both the app badges and the admin dashboard. It's paid credits,
     * or the free lifetime searches still unspent when no paid credits remain ([freeFullsLeft], from the
     * server's count). It lives HERE so nothing has to re-derive it. (Unlimited is handled by callers
     * via [isUnlimited].)
     */
    val availableFull: Int get() = if (freeFullAvailable && fullCredits == 0) freeFullsLeft else fullCredits
    val availableQc: Int get() = if (freeQcAvailable && qcCredits == 0) 1 else qcCredits

    /** True when the single available search is the free lifetime one (drives a "free" tag in admin). */
    val hasFreeFullOnly: Boolean get() = freeFullAvailable && fullCredits == 0
    val hasFreeQcOnly: Boolean get() = freeQcAvailable && qcCredits == 0

    /**
     * What this member actually pays, e.g. `"$15/month"`, `"$100/year"`, `"$45 every 3 months"` —
     * for cohorts like `thrivecart_9` whose plan KEY is shared but whose price is not (Stripe is the
     * source of truth for [planAmountCents]/[planInterval], not the key). Null when either field is
     * missing (old server, or a plan with no list price) or the amount isn't positive, so callers can
     * fall back to a price-free sentence instead of showing "$0" or "null/month".
     */
    val priceLabel: String? get() {
        val cents = planAmountCents ?: return null
        val interval = planInterval ?: return null
        if (cents <= 0) return null
        val dollars = cents / 100
        val remainderCents = cents % 100
        val amount = if (remainderCents == 0) {
            "$$dollars"
        } else {
            val paddedCents = if (remainderCents < 10) "0$remainderCents" else remainderCents.toString()
            "$$dollars.$paddedCents"
        }
        return when (interval) {
            "month" -> "$amount/month"
            "year" -> "$amount/year"
            else -> "$amount every $interval"
        }
    }

    /** Human-readable membership line for the profile: what is left this week, a search pack's
     *  balance, the one free analysis still waiting to be used, or nothing left. */
    val balanceLabel: String get() = when {
        // An organisation account has no weekly limit, so it never reads "this week".
        isLibrary -> packLabel
        isUnlimited -> "Active membership"
        freeFullAvailable -> if (freeFullsLeft == 1) "1 free analysis" else "$freeFullsLeft free analyses"
        else -> "No active subscription"
    }

    /** "9/250" (searches used / granted) for an account holding a search pack; the profile's
     *  "Searches" row: numbers only, no words. Falls back to the bare remaining count from a
     *  server that predates [packTotal]. */
    val packLabel: String get() {
        if (packTotal <= 0) return groupThousands(packRemaining)
        val total = maxOf(packTotal, packRemaining)
        return "${groupThousands(total - packRemaining)}/${groupThousands(total)}"
    }

    /** Single-number badge for the active search mode (shown beside the Quick/Full toggle). */
    val fullCount: String get() = countBadge(availableFull)
    val quickCount: String get() = countBadge(availableQc)

    private fun countBadge(credits: Int): String = when {
        isLibrary -> packRemaining.toString()
        isUnlimited -> "✓"
        else -> credits.toString()
    }

    /** True when the subscription needs attention — drives a warning color in the UI. */
    val isPastDue: Boolean get() = status == "past_due"

    /**
     * One-line subscription status for the profile: renewal date, past-due, or canceled.
     * Null when there's nothing meaningful to show (e.g. free tier) so the UI can omit the line.
     */
    val statusLabel: String? get() = when {
        // Paused is checked FIRST: a paused member is still `isUnlimited` for the remainder of the
        // month they paid for, so every unlimited branch below would otherwise claim it renews.
        status == "paused" && !currentPeriodEnd.isNullOrBlank() -> "Paused · resumes ${formatIsoDate(currentPeriodEnd)}"
        status == "paused" -> "Paused"
        isUnlimited && status == "active" && !currentPeriodEnd.isNullOrBlank() -> "Active · renews ${formatIsoDate(currentPeriodEnd)}"
        isUnlimited && status == "canceling" && !currentPeriodEnd.isNullOrBlank() -> "Active · ends ${formatIsoDate(currentPeriodEnd)}"
        isUnlimited -> "Active membership"
        status == "past_due" -> "Payment past due. Update your card"
        status == "canceling" -> if (!currentPeriodEnd.isNullOrBlank()) "Cancels ${formatIsoDate(currentPeriodEnd)}" else "Cancels at period end"
        status == "canceled" -> "Subscription canceled"
        status == "active" && !currentPeriodEnd.isNullOrBlank() -> "Renews ${formatIsoDate(currentPeriodEnd)}"
        else -> null
    }

    /** Active access (incl. a subscription that's set to cancel at period end, or legacy unlimited).
     *  Drives functional gating + whether "Manage Subscription" (reactivate) is offered — distinct
     *  from how the membership is DISPLAYED (see [isCanceledMembership]). */
    val isActiveSubscriber: Boolean get() =
        isUnlimited || status == "active" || status == "canceling" || status == "paused"

    /**
     * Anyone with a live or pending paid relationship: unlimited access, a library (search pack)
     * account, or a subscription in any state that still counts (`active`, `canceling`, `past_due`,
     * `paused`). Wider than [isActiveSubscriber] on purpose (it adds packs and past-due). The
     * free-analyses banner is never shown to these accounts; it is for people with no subscription at all.
     */
    val hasAnySubscription: Boolean get() =
        isUnlimited || isLibrary || status in SUBSCRIPTION_STATUSES

    /**
     * Display-only: show "No Active Membership / Click to get credits" instead of a plan pill. True
     * for the free tier and for a canceled/canceling subscription — i.e. any time there's no live
     * paid plan. `past_due` is excluded (it keeps its plan pill + "update your card" warning), as are
     * `active` and legacy `unlimited`. Credits are shown separately and are unaffected by this.
     *
     * "gifted" is here for the LAPSED case: once a comped membership's window passes, [isUnlimited]
     * goes false but the status string stays "gifted" forever. Without this the user would be shown
     * a plan pill for a membership they no longer have, and no way to buy one.
     */
    val showNoActiveMembership: Boolean get() =
        !isUnlimited && !isLibrary && (status == "free" || status == "canceling" || status == "canceled" || status == "gifted")

    /** Subset of [showNoActiveMembership]: the user had a subscription and canceled it. Used to
     *  SUPPRESS the "Cancels …" / "Subscription canceled" status line (the pill already says it). */
    val isCanceledMembership: Boolean get() = status == "canceling" || status == "canceled"

    /** While `canceling` the subscription is still live, so "get credits" must REACTIVATE (portal),
     *  not start a new checkout (which would create a duplicate, double-charging subscription).
     *  Excludes `thrivecart_9`: their subscription lives on a separate Stripe account this app
     *  cannot write to, so there is nothing here to reactivate — they keep the Manage Subscription
     *  (portal) row instead, which routes them to that account's own billing portal. */
    val canReactivate: Boolean get() = status == "canceling" && plan != "thrivecart_9"

    /** True when the in-app "Resume subscription" action applies: paused, and NOT `thrivecart_9`
     *  (whose pause lives on a separate Stripe account this app cannot resume). */
    val canResumeInApp: Boolean get() = isPaused && plan != "thrivecart_9"
}

/** The `status` values of an account that has, or is about to lose, a paid subscription. */
private val SUBSCRIPTION_STATUSES = setOf("active", "canceling", "past_due", "paused")

/** What a new account gets before the server has said (a signed-out visitor whose pricing fetch has not landed): keep in step with the server's FreeSearches.DEFAULT_LIMIT. */
const val DEFAULT_FREE_FULL_LIMIT = 3

/**
 * The line on the search screen that prices the next tap before it is made, so the paywall reads as
 * "the free ones are used up" rather than an ambush; null when there is nothing to promise (the screen
 * then shows no pill).
 *
 * [entitlement] is null for a signed-out visitor, who is promised [signedOutLimit] (the admin-set
 * number from GET /api/pricing; [DEFAULT_FREE_FULL_LIMIT] until that arrives or from a server that
 * predates the setting). A signed-in account reads its own limit and what is left from the server;
 * a server that predates three free searches reports a limit of 1 (or nothing), which keeps the old
 * wording. A limit of 0, or nothing left, gives null.
 */
fun freeAnalysesNudge(entitlement: Entitlement?, signedOutLimit: Int?): String? {
    if (entitlement == null) {
        val n = signedOutLimit ?: DEFAULT_FREE_FULL_LIMIT
        return when {
            n <= 0 -> null
            n == 1 -> "Your first analysis is completely free"
            else -> "Your first $n analyses are completely free"
        }
    }
    val limit = entitlement.freeFullLimit ?: 1
    val left = entitlement.freeFullsLeft
    return when {
        limit <= 0 || left <= 0 -> null
        limit == 1 -> "Your first analysis is completely free"
        left >= limit -> "Your first $limit analyses are completely free"
        left == 1 -> "You have 1 free analysis left"
        else -> "You have $left free analyses left"
    }
}

/**
 * The text of the free-analyses pill under the search bar, or null when the pill must not show. One
 * rule for web and mobile (the platforms add only their own `PAYMENTS_ENABLED` and search-screen checks):
 *
 *  - while auth is still loading: never (the session is being restored, so a member would be mistaken
 *    for a visitor);
 *  - signed out: the visitor's allowance, [freeAnalysesNudge] with no entitlement;
 *  - signed in: only once the entitlement has loaded, only when the account has NO subscription of any
 *    kind ([Entitlement.hasAnySubscription]), and only while free analyses are left.
 */
fun freeAnalysesBannerText(
    isAuthenticated: Boolean,
    isAuthLoading: Boolean,
    entitlement: Entitlement?,
    signedOutLimit: Int?
): String? = when {
    isAuthLoading -> null
    !isAuthenticated -> freeAnalysesNudge(null, signedOutLimit)
    entitlement == null || entitlement.hasAnySubscription -> null
    else -> freeAnalysesNudge(entitlement, signedOutLimit)
}

/** 1240 -> "1,240". */
private fun groupThousands(n: Int): String {
    val digits = n.toString()
    if (digits.length <= 3) return digits
    return digits.reversed().chunked(3).joinToString(",").reversed()
}

private val SHORT_MONTHS = listOf(
    "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
)

/** "2026-06-30T14:19:56+00:00" -> "Jun 30, 2026". Returns the input unchanged if it can't parse. */
private fun formatIsoDate(iso: String): String {
    val parts = iso.substringBefore("T").split("-")
    if (parts.size < 3) return iso
    val month = parts[1].toIntOrNull()?.let { SHORT_MONTHS.getOrNull(it - 1) } ?: return iso
    val day = parts[2].toIntOrNull()?.toString() ?: parts[2]
    return "$month $day, ${parts[0]}"
}
